// Resolution status badge component - 65 lines ✅
// @requirement SPEC-KB-SEARCH-001/FR-08: Filter by resolution status

import { CheckCircle, Clock, Archive, AlertCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

interface ResolutionStatusBadgeProps {
  status: 'validated' | 'pending' | 'deprecated' | 'draft'
  isActive?: boolean
  className?: string
}

interface StatusConfig {
  icon: React.ReactNode
  label: string
  bgColor: string
  textColor: string
  borderColor: string
}

const STATUS_CONFIGS: Record<string, StatusConfig> = {
  validated: {
    icon: <CheckCircle className="h-3 w-3" />,
    label: 'Validated',
    bgColor: 'bg-kaycoo-teal',
    textColor: 'text-white',
    borderColor: 'border-kaycoo-teal',
  },
  pending: {
    icon: <Clock className="h-3 w-3" />,
    label: 'Pending Review',
    bgColor: 'bg-kaycoo-blue',
    textColor: 'text-white',
    borderColor: 'border-kaycoo-blue',
  },
  deprecated: {
    icon: <Archive className="h-3 w-3" />,
    label: 'Deprecated',
    bgColor: 'bg-kaycoo-charcoal-2',
    textColor: 'text-white',
    borderColor: 'border-kaycoo-charcoal-2',
  },
  draft: {
    icon: <AlertCircle className="h-3 w-3" />,
    label: 'Draft',
    bgColor: 'bg-kaycoo-white-2',
    textColor: 'text-kaycoo-charcoal',
    borderColor: 'border-kaycoo-white-3',
  },
}

export function ResolutionStatusBadge({
  status,
  isActive = true,
  className,
}: ResolutionStatusBadgeProps): JSX.Element {
  const config = STATUS_CONFIGS[status] || STATUS_CONFIGS.draft

  return (
    <Badge
      className={cn(
        'inline-flex items-center gap-1 px-2 py-1',
        isActive
          ? cn(config.bgColor, config.textColor, config.borderColor)
          : 'bg-kaycoo-white-2 text-kaycoo-charcoal-2 border-kaycoo-white-3',
        className
      )}
    >
      {config.icon}
      <span className="text-xs font-medium">{config.label}</span>
    </Badge>
  )
}
